// @ts-nocheck
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Menu,
  MenuItem,
  Box,
  TextField,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import { TableCellWithError } from "./SessionPositionEditableSheetTableCell";
// import { StyledTableCell } from "../SessionPositionEditableSheet";
import { getCssVariableHandler } from "../../../../../utils/styles/getCssVariables";
import { CustomInput } from "../../../../../components/Custom/Input";
import { apiRequest } from "../../../../../services/apiService";
import { alertDialogAciton } from "../../../../../store/actions/auth";
import { useDispatch } from "react-redux";
import LineClamp from "../../../../positions/components/LineClamp";
import _ from "lodash";

const StyledMenu = styled(Menu)(({ theme }) => ({
  "& .MuiPaper-root": {
    border: "1px solid",
    borderColor: getCssVariableHandler({ variableName: "--primary-pink-hex" }),
    backgroundColor: "#2A2A2A",
    color: theme.palette.common.white,
    borderRadius: "0.25rem",
    padding: "0 !important",
    maxHeight: "40vh",
    overflow: "auto",
  },
  "& .MuiMenu-list": {
    padding: "0 !important",
    maxHeight: "inherit",
    overflowY: "auto",
    borderRadius: "0.25rem",
  },
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  backgroundColor: "#2A2A2A",
  color: theme.palette.common.white,
  fontSize: "0.875rem",
  "&:hover": {
    backgroundColor: "#3A3A3A",
  },
}));

export const SheetPositionDropdown = ({
  onChange,
  onContextMenu,
  customError,
  text,
  item,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const anchorRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchString, setSearchString] = useState("");
  const [options, setOptions] = useState([
    {
      positionId: item?.positionId,
      positionName: item?.positionName,
    },
  ]);
  const dispatch = useDispatch();
  const inputRef = useRef(null);

  const handleBlur = () => {
    setIsEditing(false);
    setSearchString("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleBlur();
    } else if (e.key === "Escape") {
      handleBlur();
    }
  };

  const handleClose = () => {
    if (inputRef.current) {
      inputRef.current.blur();
    }
    handleBlur();
  };

  const handleSelect = (option) => {
    onChange(option);
    handleBlur();
    handleClose();
  };

  const fetchPositions = useCallback(async () => {
    setIsLoading(true);
    const { response, error } = await apiRequest({
      method: "GET",
      url: "fetchPositions",
      params: {
        name: searchString,
        limit: 20,
        page: 1,
      },
      isToken: true,
    });
    if (response?.status == 200) {
      setIsLoading(false);
      setOptions(
        response?.data?.data?.positions?.map((p) => ({
          positionName: p?.positionName,
          positionId: p?._id,
        })),
      );
    } else if (response?.status !== 200 || error) {
      setIsLoading(false);
      dispatch(
        alertDialogAciton({
          open: true,
          title: "Error",
          message: error?.response?.data?.message || "Failed to get positions",
        }),
      );
    }
  }, [searchString, dispatch]);

  useEffect(() => {
    if (isEditing) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 0);

      return () => clearTimeout(timer);
    }
  }, [isEditing]);

  useEffect(() => {
    if (!isEditing) return;
    const debounceFetch = _.debounce(fetchPositions, 300);
    debounceFetch();
    return () => debounceFetch.cancel();
  }, [fetchPositions, isEditing]);

  return (
    <TableCellWithError
      customError={customError ?? null}
      onContextMenu={onContextMenu}
    >
      <Box
        ref={anchorRef}
        onClick={() => {
          setIsEditing(true);
        }}
        sx={{ cursor: "pointer", width: "100%" }}
      >
        {isEditing ? (
          <TextField
            inputRef={inputRef}
            autoFocus
            variant="standard"
            value={searchString}
            onChange={(e) => setSearchString(e.target.value)}
            fullWidth
            onKeyDown={(e) => {
              e.stopPropagation();
              handleKeyDown(e);
            }}
            sx={{
              width: "100%",
              backgroundColor: "#2A2A2A",
              "& .MuiInputBase-input": {
                color: "white",
                fontSize: "0.875rem",
                padding: "4px",
              },
              "& .MuiInput-underline:before": { borderBottom: "none" },
              "& .MuiInput-underline:after": { borderBottom: "none" },
              "& .MuiInputBase-root": {
                fontSize: "inherit",
                padding: 0,
              },
            }}
          />
        ) : (
          <LineClamp text={text} maxLines={1} />
        )}
      </Box>

      {isEditing && (
        <StyledMenu
          open={isEditing}
          anchorEl={anchorRef.current}
          onClose={handleClose}
          disableAutoFocus
          disableEnforceFocus
          disableRestoreFocus
          disableAutoFocusItem
          MenuListProps={{
            autoFocusItem: false,
          }}
          slotProps={{
            paper: {
              sx: {
                minWidth: anchorRef.current?.offsetWidth,
                overflowY: "hidden !important",
              },
            },
          }}
        >
          {isLoading ? (
            <StyledMenuItem>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  justifyContent: "center",
                }}
              >
                <CircularProgress size={20} />
              </Box>
            </StyledMenuItem>
          ) : (
            options?.map((option, index) => (
              <StyledMenuItem
                sx={{
                  width: "100%",
                }}
                key={index}
                onClick={() => handleSelect(option)}
              >
                {option?.positionName}
              </StyledMenuItem>
            ))
          )}
        </StyledMenu>
      )}
    </TableCellWithError>
  );
};

SheetPositionDropdown.propTypes = {
  value: PropTypes.any,
  options: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  render: PropTypes.node.isRequired,
  onContextMenu: PropTypes.func,
  customError: PropTypes.string || undefined,
  item: PropTypes.object,
  text: PropTypes.string,
};